// VerifyEmail.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import AlertMessage from '../../components/AlertMessage';
import LoadingWidget from '../../components/LoadingWidget';

// Helper to read any cookie by name
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const VerifyEmail = () => {
  const { key } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // If redirected from login, we land here without a key
  const emailFromLogin = location.state?.email || '';
  const fromLogin = location.state?.fromLogin || false;

  const [status, setStatus] = useState(key ? 'verifying' : 'pending'); // verifying | success | error | pending
  const [alert, setAlert] = useState(null);
  const [resendEmail, setResendEmail] = useState(emailFromLogin);
  const [resending, setResending] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  // Only run verification if a key is present in the URL
  useEffect(() => {
    if (!key) return;

    const verifyEmail = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/accounts/confirm-email/${key}/`,
          { withCredentials: true }
        );
        if (response.status === 200) {
          setStatus('success');
          setAlert({
            type: 'success',
            title: 'Email Verified!',
            message: 'Your email has been verified. Redirecting to login...',
            duration: 2500
          });
          setTimeout(() => navigate('/login'), 2500);
        }
      } catch (error) {
        setStatus('error');
        setAlert({
          type: 'error',
          title: 'Verification Failed',
          message: error.response?.data?.detail || 'The verification link is invalid or has expired.',
          duration: 2500
        });
      }
    };

    verifyEmail();
  }, [key, API_URL, navigate]);

  const handleResend = async () => {
    if (!resendEmail) return;
    setResending(true);
    setAlert(null);

    try {
      // Read the csrftoken cookie and attach it as a header so Django's
      // CSRF middleware accepts the POST request.
      const csrfToken = getCookie('csrftoken');

      await axios.post(
        `${API_URL}/rest-auth/resend-verification/`,
        { email: resendEmail },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken && { 'X-CSRFToken': csrfToken }),
          }
        }
      );

      setAlert({
        type: 'success',
        title: 'Email Sent!',
        message: 'A new verification link has been sent. Please check your inbox.',
        duration: 2500
      });
    } catch (err) {
      setAlert({
        type: 'error',
        title: 'Failed to Resend',
        message: err.response?.data?.detail || 'Could not resend the email. Please try again.',
        duration: 2500
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      {alert && (
        <AlertMessage
          type={alert.type}
          title={alert.title}
          message={alert.message}
          duration={alert.duration}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">

          {/* Verifying state */}
          {status === 'verifying' && (
            <>
              <LoadingWidget />
              <h2 className="mt-4 text-xl font-semibold text-gray-900">
                Verifying Your Email
              </h2>
              <p className="mt-2 text-gray-600">
                Please wait while we verify your email address...
              </p>
            </>
          )}

          {/* Success state */}
          {status === 'success' && (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="mt-4 text-xl font-semibold text-gray-900">Email Verified!</h2>
              <p className="mt-2 text-gray-600">
                Your email has been verified. You will be redirected to the login page shortly.
              </p>
            </>
          )}

          {/* Error state — bad/expired key, offer resend */}
          {status === 'error' && (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="mt-4 text-xl font-semibold text-gray-900">Verification Failed</h2>
              <p className="mt-2 text-gray-600">
                The verification link is invalid or has expired. Enter your email below to request a new one.
              </p>
              <div className="mt-6 space-y-3">
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button
                  onClick={handleResend}
                  disabled={resending || !resendEmail}
                  className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resending ? 'Sending...' : 'Resend Verification Email'}
                </button>
              </div>
            </>
          )}

          {/* Pending state — redirected here from login because account is unverified */}
          {status === 'pending' && (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="mt-4 text-xl font-semibold text-gray-900">
                Verify Your Email
              </h2>
              <p className="mt-2 text-gray-600">
                {fromLogin
                  ? 'Your account is not yet verified. Please check your inbox for the verification email, or request a new one below.'
                  : 'Enter your email address to receive a verification link.'}
              </p>
              <div className="mt-6 space-y-3">
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button
                  onClick={handleResend}
                  disabled={resending || !resendEmail}
                  className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resending ? 'Sending...' : 'Send Verification Email'}
                </button>
              </div>
            </>
          )}

          <div className="mt-6">
            <Link
              to="/login"
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              ← Back to Login
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default VerifyEmail;